/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { RouterLink } from "vue-router";
import { EventWithShop } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { getNextEventOccurrence } from "../../../utils/eventSchedule";

export const EventCard = defineComponent({
  props: {
    event: {
      type: Object as PropType<EventWithShop>,
      required: true,
    },
    city: {
      type: String,
      default: "zurich",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "GEL",
    },
  },
  setup(props) {
    const { locale, t } = useLocalization();
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale.value || "en", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    // For scheduled (esp. recurring) events, show the next upcoming occurrence
    // rather than the base `date`, which can point at a past first occurrence.
    const displayDate = () => {
      const next = getNextEventOccurrence(props.event);
      return formatDate(next ? next.toISOString() : props.event.date);
    };

    return () => {
      const imageUrl = props.event.image || null;
      const shopSlug = props.event.shop?.slug || "demo";
      const eventUrl = `/restaurant/${shopSlug}/reserve/events/${props.event.id}`;

      if (props.compact) {
        // Compact card for homepage "Top Events" section
        return (
          <article
            class="group rounded-2xl bg-white border border-gray-100 motion-safe:hover:-translate-y-1 hover:shadow-lg hover:border-gray-200 motion-safe:active:-translate-y-0.5 active:shadow-sm"
            style="transition: transform 300ms var(--ease-spring), box-shadow 300ms ease, border-color 200ms ease;"
          >
            {/* Image */}
            <RouterLink to={eventUrl}>
              <div class="aspect-[4/3] overflow-hidden cursor-pointer bg-gray-100 rounded-t-2xl card-img-overlay">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={props.event.title}
                    loading="lazy"
                    class="w-full h-full object-cover motion-safe:group-hover:scale-105 img-fade"
                    style="transition: transform 500ms var(--ease-spring), opacity 400ms ease;"
                    onLoad={(e: Event) => {
                      (e.target as HTMLImageElement).classList.add("loaded");
                    }}
                    onError={(e: Event) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    class="w-full h-full flex items-center justify-center"
                    style="background: linear-gradient(135deg, #f8f7f5 0%, #eceae6 100%);"
                  >
                    <span
                      class="material-symbols-outlined text-4xl text-gray-300/60"
                      aria-hidden="true"
                    >
                      event
                    </span>
                  </div>
                )}
              </div>
            </RouterLink>

            {/* Content */}
            <div class="space-y-3 px-4 md:px-5 pb-5 pt-4">
              {/* Tags */}
              {props.event.tags && props.event.tags.length > 0 && (
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-gray-500 tracking-wide uppercase">
                    {props.event.tags[0]}
                  </span>
                </div>
              )}

              {/* Title */}
              <RouterLink to={eventUrl}>
                <h3 class="text-xl font-medium text-gray-900 leading-tight cursor-pointer">
                  {props.event.title}
                </h3>
              </RouterLink>

              {/* Subtitle */}
              {props.event.subtitle && (
                <p class="text-sm text-gray-600 font-light">
                  {props.event.subtitle}
                </p>
              )}

              {/* Shop Info */}
              {props.event.shop && (
                <RouterLink
                  to={eventUrl}
                  class="flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
                  style="transition: color 200ms ease;"
                >
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    restaurant
                  </span>
                  <span class="text-sm font-light">
                    {props.event.shop.name}
                  </span>
                </RouterLink>
              )}

              {/* Date & Time */}
              <div class="flex items-center gap-4 text-gray-600">
                <div class="flex items-center gap-1.5">
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    calendar_today
                  </span>
                  <span class="text-sm font-light">
                    {displayDate()}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div class="pt-3 border-t border-gray-100 flex justify-center">
                <RouterLink
                  to={eventUrl}
                  class="inline-flex items-center min-h-[44px] text-sm font-medium"
                  style="color: var(--accent);"
                  aria-label={t("customer.home.view_details_for", [
                    props.event.title,
                  ])}
                >
                  {t("customer.home.view_details")}
                  <span
                    class="material-symbols-outlined text-sm ml-1.5 motion-safe:group-hover:translate-x-1"
                    style="transition: transform 200ms var(--ease-spring);"
                    aria-hidden="true"
                  >
                    arrow_forward
                  </span>
                </RouterLink>
              </div>
            </div>
          </article>
        );
      }

      // Full card for events page
      return (
        <div
          class="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 group"
          style="transition: box-shadow 300ms ease, border-color 200ms ease;"
        >
          <div class="flex flex-col md:flex-row">
            {/* Event Image */}
            <RouterLink
              to={eventUrl}
              class="md:w-72 h-48 md:h-auto overflow-hidden bg-gray-100"
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={props.event.title}
                  class="w-full h-full object-cover motion-safe:group-hover:scale-105"
                  style="transition: transform 500ms var(--ease-spring);"
                  onError={(e: Event) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div
                  class="w-full h-full flex items-center justify-center"
                  style="background: linear-gradient(135deg, #f8f7f5 0%, #eceae6 100%);"
                >
                  <span
                    class="material-symbols-outlined text-4xl text-gray-300/60"
                    aria-hidden="true"
                  >
                    event
                  </span>
                </div>
              )}
            </RouterLink>

            {/* Event Details */}
            <div class="flex-1 p-6">
              <div class="flex flex-col h-full justify-between">
                <div>
                  {/* Tags */}
                  <div class="flex flex-wrap gap-2 mb-3">
                    {props.event.tags.map((tag) => (
                      <span
                        key={tag}
                        class="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-light rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Title */}
                  <RouterLink to={eventUrl}>
                    <h3
                      class="text-2xl font-medium text-gray-900 mb-2 group-hover:text-gray-700 cursor-pointer"
                      style="transition: color 200ms ease;"
                    >
                      {props.event.title}
                    </h3>
                  </RouterLink>

                  {/* Shop Info */}
                  {props.event.shop && (
                    <RouterLink
                      to={eventUrl}
                      class="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 mb-3"
                      style="transition: color 200ms ease;"
                    >
                      <span
                        class="material-symbols-outlined text-base"
                        aria-hidden="true"
                      >
                        restaurant
                      </span>
                      <span class="text-sm font-light">
                        {props.event.shop.name}
                      </span>
                    </RouterLink>
                  )}

                  {/* Date & Time */}
                  <div class="flex items-center gap-4 text-gray-600 mb-3">
                    <div class="flex items-center gap-1.5">
                      <span
                        class="material-symbols-outlined text-lg"
                        aria-hidden="true"
                      >
                        calendar_today
                      </span>
                      <span class="font-light">
                        {displayDate()}
                      </span>
                    </div>
                    {props.event.time && (
                      <div class="flex items-center gap-1.5">
                        <span
                          class="material-symbols-outlined text-lg"
                          aria-hidden="true"
                        >
                          schedule
                        </span>
                        <span class="font-light">{props.event.time}</span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {props.event.description && (
                    <p class="text-gray-600 font-light line-clamp-2 mb-4">
                      {props.event.description}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div class="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    {props.event.price_from ? (
                      <>
                        <span class="text-sm text-gray-500 font-light">
                          {t("customer.home.from")}
                        </span>
                        <div class="text-xl font-light text-gray-900">
                          {props.event.shop?.currency || props.currency}
                          {props.event.price_from}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div class="flex items-center gap-4">
                    <RouterLink
                      to={eventUrl}
                      class="px-4 py-2 rounded-lg font-light hover:opacity-90 flex items-center"
                      style="background-color: var(--accent); color: #fff; transition: opacity 200ms ease;"
                    >
                      {t("customer.home.view_details")}
                      <span
                        class="material-symbols-outlined text-sm ml-2"
                        aria-hidden="true"
                      >
                        arrow_forward
                      </span>
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
